import { headingLevels } from './heading-levels'
import { WRAP_RATIO } from './normalizePastedText'

const PARAGRAPH_GAP_RATIO = 1.25
const HEADING_RATIO = 1.15
const MAX_LINE_SPACING = 2
const SAME_GAP_RATIO = 0.1

export const SAME_LINE_TOLERANCE = 1
export const BOLD_FONT = /bold|black|heavy/i
export const ITALIC_FONT = /italic|oblique/i

export type Run = { text: string; bold: boolean; italic: boolean }

/** One visual line of text. `y` grows downwards within a page. */
export type Line = {
  page: object
  y: number
  size: number
  runs: Run[]
}

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const lineText = (line: Line) =>
  line.runs
    .map(run => run.text)
    .join('')
    .trim()

/**
 * The line spacing is the smallest gap that occurs more than once. The most
 * common gap is not enough: minutes and notes are mostly one- and two-line
 * paragraphs, so the gap between paragraphs can outnumber it.
 */
const lineGapOf = (gaps: number[]) => {
  const sorted = [...gaps].sort((a, b) => a - b)
  return (
    sorted.find((gap, index) => {
      const next = sorted[index + 1]
      return next !== undefined && next - gap <= gap * SAME_GAP_RATIO
    }) ?? sorted[0]
  )
}

/**
 * A change of font size or a wider-than-usual vertical gap starts a new
 * paragraph. Across a page break there is no gap to measure, so a short last
 * line decides it instead.
 */
const splitParagraphs = (lines: Line[], longest: number) => {
  const gaps = lines.flatMap((line, index) => {
    const previous = lines[index - 1]
    return previous?.page === line.page ? [line.y - previous.y] : []
  })
  const lineGap = lineGapOf(gaps.filter(gap => gap >= SAME_LINE_TOLERANCE))

  const paragraphs: Line[][] = []
  lines.forEach((line, index) => {
    const previous = lines[index - 1]
    const last = paragraphs[paragraphs.length - 1]

    const maxGap =
      Math.min(
        lineGap ?? Infinity,
        line.size ? line.size * MAX_LINE_SPACING : Infinity
      ) * PARAGRAPH_GAP_RATIO

    const continues =
      previous &&
      last &&
      previous.size === line.size &&
      (previous.page === line.page
        ? line.y > previous.y && line.y - previous.y <= maxGap
        : lineText(previous).length >= longest * WRAP_RATIO)

    if (continues) last.push(line)
    else paragraphs.push([line])
  })
  return paragraphs
}

/**
 * Inside a paragraph, a line that stops well short of the longest line was
 * ended by the author ("Present:" followed by one name per line), so the
 * next line starts on its own instead of being joined on.
 */
const splitRows = (paragraph: Line[], longest: number) => {
  const rows: Line[][] = []
  paragraph.forEach((line, index) => {
    const previous = paragraph[index - 1]
    const row = rows[rows.length - 1]
    if (row && previous && lineText(previous).length >= longest * WRAP_RATIO)
      row.push(line)
    else rows.push([line])
  })
  return rows
}

const runsToHtml = (runs: Run[]) => {
  const merged: Run[] = []
  runs.forEach(run => {
    const last = merged[merged.length - 1]
    if (last && last.bold === run.bold && last.italic === run.italic)
      last.text += run.text
    else merged.push({ ...run })
  })

  return merged
    .map(({ text, bold, italic }) => {
      let html = escapeHtml(text)
      if (italic) html = `<em>${html}</em>`
      if (bold) html = `<strong>${html}</strong>`
      return html
    })
    .join('')
}

const paragraphRuns = (paragraph: Line[]) =>
  paragraph.flatMap((line, index) => {
    const runs = line.runs.map(run => ({
      ...run,
      text: run.text.replace(/\s+/g, ' '),
    }))
    const first = runs[0]
    const last = runs[runs.length - 1]
    if (first) first.text = first.text.trimStart()
    if (last)
      last.text = `${last.text.trimEnd()}${
        index < paragraph.length - 1 ? ' ' : ''
      }`
    return runs.filter(run => run.text)
  })

const bodySizeOf = (lines: Line[]) => {
  const weight = new Map<number, number>()
  lines.forEach(line =>
    weight.set(line.size, (weight.get(line.size) ?? 0) + lineText(line).length)
  )
  return [...weight].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0
}

/**
 * Rebuilds paragraphs and headings from text laid out as positioned lines,
 * the way a PDF page holds it. Text larger than the body size becomes a
 * heading, and bold and italic runs are kept.
 */
export function linesToHtml(allLines: Line[]): string | null {
  const lines = allLines.filter(line => lineText(line))
  if (!lines.length) return null

  const bodySize = bodySizeOf(lines)
  const longest = Math.max(...lines.map(line => lineText(line).length))
  const paragraphs = splitParagraphs(lines, longest).map(paragraph => {
    const size = Math.min(...paragraph.map(line => line.size))
    const heading = bodySize > 0 && size >= bodySize * HEADING_RATIO
    return { paragraph, heading, size }
  })
  const levels = headingLevels(
    paragraphs.flatMap(({ heading, size }) => (heading ? [size] : []))
  )

  return paragraphs
    .map(({ paragraph, heading, size }) => {
      const tag = heading ? `h${levels.get(size)}` : 'p'
      return splitRows(paragraph, longest)
        .map(row => `<${tag}>${runsToHtml(paragraphRuns(row))}</${tag}>`)
        .join('')
    })
    .join('<p><br></p>')
}
